import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, GitCompare, RefreshCw, CheckCircle2, AlertTriangle, Info,
  Link as LinkIcon, ChevronRight, ChevronDown, Download,
} from "lucide-react";
import { api } from "../../lib/api";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}`);
const dateShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return iso.slice(0, 10); }
};

/**
 * Bank Reconciliation card — matches paid Stripe checkouts against actual
 * deposits landing in FC-linked founder banks. Highlights any payment that
 * didn't arrive and any deposit that didn't originate from Stripe.
 */
export default function BankReconciliationCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState(30);
  const [showMatched, setShowMatched] = useState(false);

  const load = async (overrideDays) => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/admin-auth/reconciliation?days=${overrideDays ?? days}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reconciliation.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, []);

  const changeDays = async (d) => {
    setDays(d);
    await load(d);
  };

  const downloadCsv = async () => {
    try {
      const resp = await api.get(`/admin-auth/reconciliation.csv?days=${days}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `elevare-reconciliation-${days}d-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded — hand it to your bookkeeper.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export CSV.");
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 flex items-center justify-center min-h-[140px]">
        <Loader2 className="h-4 w-4 animate-spin text-[#c9a961]" />
      </div>
    );
  }

  const s = data?.summary || {};
  const anyLinked = (s.banks_checked || 0) > 0;
  const anyUnmatchedPayments = (s.unmatched_payments_count || 0) > 0;
  const anyUnmatchedDeposits = (s.unmatched_deposits_count || 0) > 0;

  return (
    <div data-testid="reconciliation-card" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <GitCompare className="h-4 w-4 text-[#a3823f] shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Bank reconciliation</p>
            <p className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/40 mt-0.5">
              Match paid Stripe checkouts to actual deposits · Stripe fees estimated at 2.9% + $0.30
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              type="button"
              data-testid={`recon-window-${d}`}
              onClick={() => changeDays(d)}
              className={`text-[9px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border transition-colors duration-300 ${
                days === d
                  ? "bg-[#1e3d3a] text-white border-[#1e3d3a]"
                  : "bg-transparent text-[#1e3d3a]/60 border-[#1e3d3a]/15 hover:border-[#c9a961] hover:text-[#1e3d3a]"
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            type="button"
            data-testid="recon-download-csv"
            onClick={downloadCsv}
            disabled={refreshing}
            className="ml-1 rounded-full border border-[#1e3d3a]/15 p-1.5 text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961] disabled:opacity-60 transition-colors duration-300"
            title="Download CSV — hand to your bookkeeper for month-end close"
          >
            <Download className="h-3 w-3" />
          </button>
          <button
            type="button"
            data-testid="recon-refresh"
            onClick={() => load()}
            disabled={refreshing}
            className="rounded-full border border-[#1e3d3a]/15 p-1.5 text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961] disabled:opacity-60 transition-colors duration-300"
            title="Refresh"
          >
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {!anyLinked && (
        <div data-testid="recon-no-fc" className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/10 p-3 mb-4 flex items-start gap-2">
          <LinkIcon className="h-3.5 w-3.5 text-[#a3823f] shrink-0 mt-0.5" />
          <p className="text-[11px] text-[#1e3d3a]/75 leading-relaxed">
            <span className="font-medium text-[#1e3d3a]">Link a bank via Stripe Financial Connections</span> to
            enable reconciliation. Once linked, every paid checkout will be paired with its actual bank
            deposit and any discrepancy will surface here.
          </p>
        </div>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <SummaryTile
          testid="recon-tile-matched"
          label="Matched"
          value={s.matched_count || 0}
          amount={s.matched_deposit_amount}
          tone="ok"
          icon={<CheckCircle2 className="h-3 w-3" />}
        />
        <SummaryTile
          testid="recon-tile-payments"
          label="Awaiting deposit"
          value={s.unmatched_payments_count || 0}
          amount={s.unmatched_payments_amount}
          tone={anyUnmatchedPayments ? "warn" : "muted"}
          icon={<AlertTriangle className="h-3 w-3" />}
        />
        <SummaryTile
          testid="recon-tile-deposits"
          label="Uncategorized"
          value={s.unmatched_deposits_count || 0}
          amount={s.unmatched_deposits_amount}
          tone={anyUnmatchedDeposits ? "info" : "muted"}
          icon={<Info className="h-3 w-3" />}
        />
      </div>

      {/* Unmatched payments (highest signal — surface first) */}
      {anyUnmatchedPayments && (
        <Section
          testid="recon-unmatched-payments"
          title="Paid Stripe checkouts with no matching deposit"
          tone="warn"
          count={s.unmatched_payments_count}
        >
          <ul className="divide-y divide-[#1e3d3a]/6">
            {(data.unmatched_payments || []).map((u) => (
              <li key={u.session_id} data-testid={`recon-payment-${u.session_id}`} className="py-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[12px] text-[#1e3d3a] truncate">
                    {u.client_email || u.session_id}
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/40 ml-2">{u.lookup_key}</span>
                  </p>
                  <p className="text-[10px] text-[#1e3d3a]/55 mt-0.5">
                    Paid {dateShort(u.paid_at)} · <span className="text-[#a3823f]">{u.reason}</span>
                  </p>
                </div>
                <p className="text-[12px] tabular-nums text-[#1e3d3a] font-medium shrink-0">{money(u.amount)}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Unmatched deposits */}
      {anyUnmatchedDeposits && (
        <Section
          testid="recon-unmatched-deposits"
          title="Bank deposits with no matching Stripe checkout"
          tone="info"
          count={s.unmatched_deposits_count}
        >
          <ul className="divide-y divide-[#1e3d3a]/6">
            {(data.unmatched_deposits || []).map((d) => (
              <li key={d.txn_id} data-testid={`recon-deposit-${d.txn_id}`} className="py-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[12px] text-[#1e3d3a] truncate">{d.description || "Untitled deposit"}</p>
                  <p className="text-[10px] text-[#1e3d3a]/55 mt-0.5">
                    {dateShort(d.date)} · {d.bank_name}
                  </p>
                </div>
                <p className="text-[12px] tabular-nums text-[#1e3d3a] font-medium shrink-0">{money(d.amount)}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Matched (collapsed) */}
      {(data?.matched?.length || 0) > 0 && (
        <div className="mt-3">
          <button
            type="button"
            data-testid="recon-toggle-matched"
            onClick={() => setShowMatched(!showMatched)}
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] transition-colors duration-300"
          >
            {showMatched ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {showMatched ? "Hide" : "Show"} {s.matched_count} matched pair{s.matched_count === 1 ? "" : "s"}
          </button>
          {showMatched && (
            <ul data-testid="recon-matched-list" className="mt-2 divide-y divide-[#1e3d3a]/6 rounded-xl border border-[#1e3d3a]/8 bg-[#eae7de]/40 overflow-hidden">
              {(data.matched || []).map((m) => (
                <li key={m.session_id} data-testid={`recon-matched-${m.session_id}`} className="p-2.5 grid grid-cols-[1fr_auto] gap-2">
                  <div className="min-w-0">
                    <p className="text-[12px] text-[#1e3d3a] truncate">
                      {m.client_email || m.session_id}
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/40 ml-2">{m.lookup_key}</span>
                    </p>
                    <p className="text-[10px] text-[#1e3d3a]/55 mt-0.5">
                      Paid {dateShort(m.paid_at)} → deposited to {m.bank_name} · {m.days_delta}d delay · fee {money(m.fee_estimate)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[12px] tabular-nums text-[#1e3d3a] font-medium">{money(m.amount_paid)}</p>
                    <p className="text-[10px] text-[#4d5a24]">↓ {money(m.amount_deposited)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Banks note — some banks not FC-linked */}
      {anyLinked && s.banks_missing_fc > 0 && (
        <p data-testid="recon-partial-note" className="text-[10px] text-[#1e3d3a]/45 italic mt-3">
          {s.banks_missing_fc} bank{s.banks_missing_fc === 1 ? "" : "s"} not linked via Stripe FC — those deposits are invisible here.
        </p>
      )}
    </div>
  );
}

function SummaryTile({ label, value, amount, tone, icon, testid }) {
  const toneCls = tone === "ok"
    ? "border-[#6b7c3a]/25 bg-[#6b7c3a]/8 text-[#4d5a24]"
    : tone === "warn"
    ? "border-[#c9a961] bg-[#c9a961]/12 text-[#a3823f]"
    : tone === "info"
    ? "border-[#1e3d3a]/20 bg-[#1e3d3a]/6 text-[#1e3d3a]"
    : "border-[#1e3d3a]/10 bg-[#eae7de]/40 text-[#1e3d3a]/55";
  return (
    <div data-testid={testid} className={`rounded-xl border p-3 ${toneCls}`}>
      <p className="text-[9px] uppercase tracking-[0.22em] flex items-center gap-1">
        {icon}
        {label}
      </p>
      <p className="font-head text-2xl tabular-nums mt-0.5">{value}</p>
      <p className="text-[10px] tabular-nums opacity-70 mt-0.5">{money(amount)}</p>
    </div>
  );
}

function Section({ title, tone, count, testid, children }) {
  const toneCls = tone === "warn"
    ? "border-[#c9a961]/40 bg-[#c9a961]/6"
    : "border-[#1e3d3a]/12 bg-[#eae7de]/40";
  return (
    <div data-testid={testid} className={`rounded-xl border p-3 mt-3 ${toneCls}`}>
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-2">
        {title} <span className="text-[#1e3d3a]/40">· {count}</span>
      </p>
      {children}
    </div>
  );
}
